import { baseUrlInternal } from "config/baseUrl";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { Alert } from "react-bootstrap";
import { Exclamation, Info } from "react-bootstrap-icons";

dayjs.extend(duration);

export const isIterableArray = (array) =>
  Array.isArray(array) && !!array.length;

export const MuskEmail = (email) => {
  if (!email) return "";

  const [localPart, domain] = email.split("@");
  if (!domain) return email;

  const visiblePart = localPart.slice(0, 2); // first 2 characters
  const maskedPart = "*".repeat(Math.max(0, localPart.length - 2)); // mask the rest

  return `${visiblePart}${maskedPart}@${domain}`;
};

export function getPathFromUrl(url) {
  if (!url || !baseUrlInternal) return "";

  const fullUrl = new URL(url);
  const internalUrl = new URL(baseUrlInternal);

  // Check if origins match; if not, return the pathname as is
  if (fullUrl.origin !== internalUrl.origin) {
    return fullUrl.pathname;
  }

  let path = fullUrl.pathname;
  let internalPath = internalUrl.pathname;

  // Normalize internalPath: remove trailing '/' if present
  if (internalPath.endsWith("/")) {
    internalPath = internalPath.slice(0, -1);
  }

  // Special case: if internalPath is empty or '/', return path as is
  if (!internalPath || internalPath === "/") {
    return path;
  }

  // Check if path starts with internalPath
  if (path.startsWith(internalPath)) {
    return path.slice(internalPath.length);
  }

  return path;
}
export const getToday = () => new Date();

export const ShowError = ({ errors = {}, validationErrors = {} }) => {
  const validationErrorEntries = Object.entries(validationErrors || {});

  const allErrors = [
    ...Object.entries(errors || {}),
    ...validationErrorEntries,
  ];

  return (
    allErrors.length > 0 && (
      <div
        style={{
          background: "#ffdad7",
          color: "#f00",
          padding: "10px",
          borderRadius: "10px",
          marginTop: "12px",
          fontWeight: "bolder",
        }}
      >
        {allErrors.map(([key, error], index) => {
          const formattedKey = key
            .replace("redux-", "")
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          return (
            <div
              key={key}
              className="p-1 d-flex align-items-center gap-1 fs-12"
            >
              <Exclamation size={16} />
              {index + 1}. {error?.message || `${formattedKey} is Required`}
            </div>
          );
        })}
      </div>
    )
  );
};

export const ShowWarning = ({ warning, validationWarning = [] }) => {
  const allErrors = [
    ...Object.entries(warning || {}),
    ...validationWarning.map((error, index) => [
      `redux-${index}`,
      { message: error.message },
    ]),
  ];

  if (allErrors.length === 0) return null;

  return (
    <div
      style={{
        background: "#fde6d8",
        color: "#ac5a2b",
        padding: "10px",
        borderRadius: "10px",

        marginTop: "12px",
        fontWeight: "bolder",
      }}
    >
      {allErrors.map(([key, error], index) => {
        const formattedKey = key
          .replace("redux-", "")
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        return (
          <div key={key} className="py-1 d-flex align-items-center gap-1 fs-12">
            <Info size={16} />
            {index + 1}. {error?.message || `${formattedKey} is required`}
          </div>
        );
      })}
    </div>
  );
};

//===============================
// Breakpoints
//===============================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1540,
};

export const getItemFromStore = (key, defaultValue, store = localStorage) => {
  try {
    return store.getItem(key) === null
      ? defaultValue
      : JSON.parse(store.getItem(key));
  } catch {
    return store.getItem(key) || defaultValue;
  }
};

export const setItemToStore = (key, payload, store = localStorage) =>
  store.setItem(key, payload);

export const getStoreSpace = (store = localStorage) =>
  parseFloat(
    (
      escape(encodeURIComponent(JSON.stringify(store))).length /
      (1024 * 1024)
    ).toFixed(2)
  );

//===============================
// Cookie
//===============================
export const getCookieValue = (name) => {
  const value = document.cookie.match(
    "(^|[^;]+)\\s*" + name + "\\s*=\\s*([^;]+)"
  );
  return value ? value.pop() : null;
};

export const createCookie = (name, value, cookieExpireTime) => {
  const date = new Date();
  date.setTime(date.getTime() + cookieExpireTime);
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + value + expires + "; path=/";
};

export const numberFormatter = (number, fixed = 2) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(number)) >= 1.0e9
    ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + "B"
    : // Six Zeroes for Millions
    Math.abs(Number(number)) >= 1.0e6
    ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + "M"
    : // Three Zeroes for Thousands
    Math.abs(Number(number)) >= 1.0e3
    ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + "K"
    : Math.abs(Number(number)).toFixed(fixed);
};

//===============================
// Colors
//===============================
export const hexToRgb = (hexValue) => {
  let hex;
  hexValue.indexOf("#") === 0
    ? (hex = hexValue.substring(1))
    : (hex = hexValue);
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)
  );
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : null;
};

export const rgbColor = (color = colors[0]) => `rgb(${hexToRgb(color)})`;
export const rgbaColor = (color = colors[0], alpha = 0.5) =>
  `rgba(${hexToRgb(color)},${alpha})`;

export const colors = [
  "#2c7be5",
  "#00d97e",
  "#e63757",
  "#39afd1",
  "#fd7e14",
  "#02a8b5",
  "#727cf5",
  "#6b5eae",
  "#ff679b",
  "#f6c343",
];

export const themeColors = {
  primary: "#2c7be5",
  secondary: "#748194",
  success: "#00d27a",
  info: "#27bcfd",
  warning: "#f5803e",
  danger: "#e63757",
  light: "#f9fafd",
  dark: "#0b1727",
};

export const grays = {
  white: "#fff",
  100: "#f9fafd",
  200: "#edf2f9",
  300: "#d8e2ef",
  400: "#b6c1d2",
  500: "#9da9bb",
  600: "#748194",
  700: "#5e6e82",
  800: "#4d5969",
  900: "#344050",
  1000: "#232e3c",
  1100: "#0b1727",
  black: "#000",
};

export const darkGrays = {
  white: "#fff",
  1100: "#f9fafd",
  1000: "#edf2f9",
  900: "#d8e2ef",
  800: "#b6c1d2",
  700: "#9da9bb",
  600: "#748194",
  500: "#5e6e82",
  400: "#4d5969",
  300: "#344050",
  200: "#232e3c",
  100: "#0b1727",
  black: "#000",
};

export const getGrays = (isDark) => (isDark ? darkGrays : grays);

export const rgbColors = colors.map((color) => rgbColor(color));
export const rgbaColors = colors.map((color) => rgbaColor(color));

export const getColor = (name) => {
  const dom = document.documentElement;
  return getComputedStyle(dom).getPropertyValue(`--falcon-${name}`).trim();
};

//===============================
// Echarts
//===============================
export const getPosition = (pos, params, dom, rect, size) => ({
  top: pos[1] - size.contentSize[1] - 10,
  left: pos[0] - size.contentSize[0] / 2,
});
//===============================
// E-Commerce
//===============================
export const calculateSale = (base, less = 0, fix = 2) =>
  (base - base * (less / 100)).toFixed(fix);
export const getTotalPrice = (cart, baseItems) =>
  cart.reduce((accumulator, currentValue) => {
    const { id, quantity } = currentValue;
    const { price, sale } = baseItems.find((item) => item.id === id);
    return accumulator + calculateSale(price, sale) * quantity;
  }, 0);
export const getSubtotal = (items) =>
  items.reduce((acc, curr) => curr.price * curr.quantity + acc, 0);
export const getDiscountPrice = (total, discount) =>
  total - total * (discount / 100);

export const getProductsQuantity = (products) =>
  products.reduce((acc, product) => product.quantity + acc, 0);

//===============================
// Helpers
//===============================
export const getPaginationArray = (totalSize, sizePerPage) => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array = [];
  let pageNo = 1;
  while (pageNo <= noOfPages) {
    array.push(pageNo);
    pageNo = pageNo + 1;
  }
  return array;
};

export const capitalize = (str) =>
  (str?.charAt(0)?.toUpperCase() + str?.slice(1))?.replace(/-/g, " ");

export const camelize = (str) => {
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
    if (+match === 0) return ""; // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};

export const dashed = (str) => {
  return str.toLowerCase().replaceAll(" ", "-");
};

//routes helper

export const flatRoutes = (childrens) => {
  const allChilds = [];

  const flatChild = (childrens) => {
    childrens.forEach((child) => {
      if (child.children) {
        flatChild(child.children);
      } else {
        allChilds.push(child);
      }
    });
  };
  flatChild(childrens);

  return allChilds;
};

export const getFlatRoutes = (children) =>
  children.reduce(
    (acc, val) => {
      if (val.children) {
        return {
          ...acc,
          [camelize(val.name)]: flatRoutes(val.children),
        };
      } else {
        return {
          ...acc,
          unTitled: [...acc.unTitled, val],
        };
      }
    },
    { unTitled: [] }
  );

export const routesSlicer = ({ routes, columns = 3, rows }) => {
  const routesCollection = [];
  routes.map((route) => {
    if (route.children) {
      return route.children.map((item) => {
        if (item.children) {
          return routesCollection.push(...item.children);
        }
        return routesCollection.push(item);
      });
    }
    return routesCollection.push(route);
  });

  const totalRoutes = routesCollection.length;
  const calculatedRows = rows || Math.ceil(totalRoutes / columns);
  const routesChunks = [];
  for (let i = 0; i < totalRoutes; i += calculatedRows) {
    routesChunks.push(routesCollection.slice(i, i + calculatedRows));
  }
  return routesChunks;
};

export const getPageName = (pageName) => {
  return window.location.pathname.split("/").slice(-1)[0] === pageName;
};

export const copyToClipBoard = (textFieldRef) => {
  const textField = textFieldRef.current;
  textField.focus();
  textField.select();
  document.execCommand("copy");
};

export const reactBootstrapDocsUrl = "https://react-bootstrap.github.io";

export const pagination = (currentPage, size) => {
  const pages = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  let prev = currentPage - 1 - Math.floor(size / 2);

  if (currentPage - 1 - Math.floor(size / 2) < 0) {
    prev = 0;
  }
  if (currentPage - 1 - Math.floor(size / 2) > pages.length - size) {
    prev = pages.length - size;
  }
  const next = prev + size;

  return pages.slice(prev, next);
};

export const tooltipFormatter = (params) => {
  let tooltipItem = ``;
  params.forEach((el) => {
    tooltipItem =
      tooltipItem +
      `<div class='ms-1'> 
        <h6 class="text-700"><span class="fas fa-circle me-1 fs-11" style="color:${
          el.borderColor ? el.borderColor : el.color
        }"></span>
          ${el.seriesName} : ${
        typeof el.value === "object" ? el.value[1] : el.value
      }
        </h6>
      </div>`;
  });
  return `<div>
            <p class='mb-2 text-600'>
              ${
                dayjs(params[0].axisValue).isValid()
                  ? dayjs(params[0].axisValue).format("MMMM DD")
                  : params[0].axisValue
              }
            </p>
            ${tooltipItem}
          </div>`;
};

export const addIdField = (items) => {
  return items.map((item, index) => ({
    id: index + 1,
    ...item,
  }));
};

// get file size

export const getSize = (size) => {
  if (size < 1024) {
    return `${size} Byte`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/* Get A Random Number */
export const getRandomNumber = (min, max) => {
  return Math.floor(Math.random() * (max - min) + min);
};

/* get Dates between */

export const getDates = (
  startDate,
  endDate,
  interval = 1000 * 60 * 60 * 24
) => {
  const duration = endDate - startDate;
  const steps = duration / interval;
  return Array.from(
    { length: steps + 1 },
    (v, i) => new Date(startDate.valueOf() + interval * i)
  );
};

/* Get Past Dates */
export const getPastDates = (duration) => {
  let days;

  switch (duration) {
    case "week":
      days = 7;
      break;
    case "month":
      days = 30;
      break;
    case "year":
      days = 365;
      break;

    default:
      days = duration;
  }

  const date = new Date();
  const endDate = date;
  const startDate = new Date(new Date().setDate(date.getDate() - (days - 1)));
  return getDates(startDate, endDate);
};

// Add id to items in array
export const addId = (items) =>
  items.map((item, index) => ({
    id: index + 1,
    ...item,
  }));

//
export const getTimeDuration = (startDate, endDate, format = "") => {
  return dayjs.duration(endDate.diff(startDate)).format(format);
};

// Get Percentage
export const getPercentage = (number, percent) => {
  return (Number(number) / 100) * Number(percent);
};

//get chunk from array
export const chunk = (arr, chunkSize = 1, cache = []) => {
  const tmp = [...arr];
  if (chunkSize <= 0) return cache;
  while (tmp.length) cache.push(tmp.splice(0, chunkSize));
  return cache;
};
function gmod(n, m) {
  return ((n % m) + m) % m;
}

function kuwaiticalendar(today, adjust) {
  // if(adjust) {
  //     adjustmili = 1000*60*60*24*adjust;
  //     todaymili = today.getTime()+adjustmili;
  //     today = new Date(todaymili);
  // }
  // alert(today);
  var day = today.getDate();
  var month = today.getMonth();
  var year = today.getFullYear();
  var m = month + 1;
  var y = year;
  if (m < 3) {
    y -= 1;
    m += 12;
  }

  var a = Math.floor(y / 100);
  var b = 2 - a + Math.floor(a / 4);
  if (y < 1583) b = 0;
  if (y == 1582) {
    if (m > 10) b = -10;
    if (m == 10) {
      b = 0;
      if (day > 4) b = -10;
    }
  }

  var jd =
    Math.floor(365.25 * (y + 4716)) +
    Math.floor(30.6001 * (m + 1)) +
    day +
    b -
    1524;

  b = 0;
  if (jd > 2299160) {
    a = Math.floor((jd - 1867216.25) / 36524.25);
    b = 1 + a - Math.floor(a / 4);
  }
  var bb = jd + b + 1524;
  var cc = Math.floor((bb - 122.1) / 365.25);
  var dd = Math.floor(365.25 * cc);
  var ee = Math.floor((bb - dd) / 30.6001);
  day = bb - dd - Math.floor(30.6001 * ee);
  month = ee - 1;
  if (ee > 13) {
    cc += 1;
    month = ee - 13;
  }
  year = cc - 4716;

  var wd = gmod(jd + 1, 7) + 1;

  var iyear = 10631 / 30;
  var epochastro = 1948084;
  var epochcivil = 1948085;

  var shift1 = 8.01 / 60;

  var z = jd - epochastro;

  var cyc = Math.floor(z / 10631);
  z = z - 10631 * cyc;
  var j = Math.floor((z - shift1) / iyear);
  var iy = 30 * cyc + j;
  z = z - Math.floor(j * iyear + shift1);
  var im = Math.floor((z + 28.5001) / 29.5);
  if (im == 13) im = 12;

  var id = z - Math.floor(29.5001 * im - 29);

  var myRes = new Array(8);

  myRes[0] = day; //calculated day (CE)
  myRes[1] = month - 1; //calculated month (CE)
  myRes[2] = year; //calculated year (CE)
  myRes[3] = jd - 1; //julian day number
  myRes[4] = wd - 1; //weekday number
  myRes[5] = id; //islamic date
  myRes[6] = im; //islamic month
  myRes[7] = iy; //islamic year

  return myRes;
}

// Slugify text
export const slugifyText = (str) =>
  str
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");

function writeIslamicDate(date, adjustment) {
  var wdNames = new Array(
    "Ahad",
    "Ithnin",
    "Thulatha",
    "Arbaa",
    "Khams",
    "Jumuah",
    "Sabt"
  );
  var iMonthNames = new Array(
    "Muharram",
    "Safar",
    "Rabi'ul Awwal",
    "Rabi'ul Akhir",
    "Jumadal Ula",
    "Jumadal Akhira",
    "Rajab",
    "Sha'ban",
    "Ramadan",
    "Shawwal",
    "Dhul Qa'ada",
    "Dhul Hijja"
  );
  var iDate = kuwaiticalendar(date, adjustment);
  var outputIslamicDate = iDate[5] + "/" + iDate[6] + "/" + iDate[7];
  return outputIslamicDate;
}

export const ageCalculator = (userinput) => {
  // collect input and convert into date format
  const dob = new Date(userinput);

  // extract the year, month, and date from user date input
  const dobYear = dob.getYear();
  const dobMonth = dob.getMonth();
  const dobDate = dob.getDate();

  // get the current date from the system
  const now = new Date();
  // extract the year, month, and date from current date
  const currentYear = now.getYear();
  const currentMonth = now.getMonth();
  const currentDate = now.getDate();

  // declare a variable to collect the age in year, month, and days
  let age = {};
  let ageString = "";
  let ageInHijri = {};

  let monthAge;
  let dateAge;

  // get years
  let yearAge = currentYear - dobYear;

  // get months
  if (currentMonth >= dobMonth)
    // get months when current month is greater
    monthAge = currentMonth - dobMonth;
  else {
    yearAge -= 1;
    monthAge = 12 + currentMonth - dobMonth;
  }

  // get days
  if (currentDate >= dobDate)
    // get days when the current date is greater
    dateAge = currentDate - dobDate;
  else {
    monthAge -= 1;
    dateAge = 31 + currentDate - dobDate;

    if (monthAge < 0) {
      monthAge = 11;
      yearAge -= 1;
    }
  }

  // group the age in a single variable
  age = {
    years: yearAge,
    months: monthAge,
    days: dateAge,
  };

  if (age.years > 0 && age.months > 0 && age.days > 0)
    ageString = ` ${age.years} years, ${age.months} months, and ${age.days} days old.`;
  else if (age.years === 0 && age.months === 0 && age.days > 0)
    ageString = `Only ${age.days} days old.`;
  // when current month and date is same as birth date and month
  else if (age.years > 0 && age.months === 0 && age.days === 0)
    ageString = ` ${age.years} years old. Happy Birthday!!`;
  else if (age.years > 0 && age.months > 0 && age.days === 0)
    ageString = `${age.years} years and ${age.months}  months old.`;
  else if (age.years === 0 && age.months > 0 && age.days > 0)
    ageString = `${age.months} months and ${age.days} days old.`;
  else if (age.years > 0 && age.months === 0 && age.days > 0)
    ageString = `${age.years} years, and ${age.days} days old.`;
  else if (age.years === 0 && age.months > 0 && age.days === 0)
    ageString = `${age.months} months old.`;
  // when current date is same as dob(date of birth)
  else {
    // toast.success("Welcome to Earth! It's first day on Earth!");
    ageString = 0;
  }

  // Get Hijri DOB(date of birth)
  const now1 = new Date(userinput);
  var hijri = writeIslamicDate(now1, 1);
  // const dayOfYear = Math.floor((new Date() - new Date(now1.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  // const hijriDate = ((now1.getFullYear() - 621.5643) * 365.24225 + dayOfYear) / 354.36707;
  // const hijriYear = Math.floor(hijriDate);
  // const hijriMonth = Math.ceil(((hijriDate - Math.floor(hijriDate)) * 354.36707) / 29.530589);
  // const hijriDay = Math.floor(((hijriDate - Math.floor(hijriDate)) * 354.36707) % 29.530589);
  const hijriArr = hijri?.split("/");
  const hijriYear = hijriArr[2];
  const hijriMonth = hijriArr[0];
  const hijriDay = hijriArr[1];
  ageInHijri = {
    hijriYear,
    hijriMonth,
    hijriDay,
  };

  // display the calculated Values
  return { age, ageString, ageInHijri };
};
